import { ClassroomId } from 'api/types'
import { ExceptionType, GOCException } from 'exceptions'
import { apiRequest } from '../api-request'
import { Guid, UrlString } from './types'
import type { BasicUserInformation, BasicUserInformationResponse, ClassInfoResponse } from './class-details.types'

export interface Student {
    firstName: string
    lastName: string
    userId: Guid
    photoUris: {
        small?: UrlString
        medium?: UrlString
        big?: UrlString
    }
}

export interface ClassBooking {
    student: Student
    currentUnitNumber: string
}

export interface ClassDetails {
    bookedStudents: ClassBooking[]
    classId: Guid
    teacherId: Guid
    teacherSsdsId: Guid
    isClosed: boolean
}

export const mapToClassDetails = (classInfo: ClassInfoResponse, users: BasicUserInformation[]): ClassDetails => {
    const userMap = new Map<Guid, BasicUserInformation>(
        users.map(u => [u.userReferenceId, u])
    )

    const bookedStudents: ClassBooking[] = classInfo.students
        .filter(s => !s.isCancelled)
        .map(s => {
            const user = userMap.get(s.studentReferenceId)
            if (!user) {
                throw new GOCException(
                    ExceptionType.HttpError,
                    `IAM user not found for studentReferenceId: ${s.studentReferenceId}`
                )
            }
            return {
                student: {
                    userId: s.studentReferenceId,
                    firstName: user.firstname,
                    lastName: user.lastname,
                    photoUris: {
                        small: user.photoUris.small,
                        medium: user.photoUris.medium,
                        big: user.photoUris.big
                    }
                },
                currentUnitNumber: ''
            }
        })

    return {
        classId: classInfo.classId,
        teacherId: classInfo.teacherReferenceId,
        teacherSsdsId: classInfo.teacherReferenceId,
        isClosed: classInfo.closedAt !== null,
        bookedStudents
    }
}

export const isBookingClassDetailsEnabled = (): boolean =>
    window.growthbook?.isOn('goc-booking-class-details') ?? false

const getClassDetailsFromBsApi = async (auth: string, activityId: ClassroomId): Promise<ClassDetails> =>
    apiRequest<ClassDetails>({
        baseUrl: process.env.REACT_APP_BS_API_URL,
        path: `/classes/${process.env.REACT_APP_ACTIVITY_ID ?? activityId}/details`,
        method: 'GET',
        auth: process.env.REACT_APP_QUALWORLD_AUTH ?? auth
    })

const getClassDetailsFromBookingApi = async (auth: string, activityId: ClassroomId): Promise<ClassDetails> => {
    const classInfo = await apiRequest<ClassInfoResponse>({
        baseUrl: process.env.REACT_APP_BOOKING_URL,
        path: `/api/class/${activityId}/info`,
        method: 'GET',
        auth
    })

    const userReferenceIds: Guid[] = classInfo.students
        .filter(s => !s.isCancelled)
        .map(s => s.studentReferenceId)

    const usersResponse = await apiRequest<BasicUserInformationResponse>({
        baseUrl: process.env.REACT_APP_IDAM_URL,
        path: '/api/user/basicUserInformation/multiple',
        method: 'POST',
        auth,
        payload: { userReferenceIds }
    })

    return mapToClassDetails(classInfo, usersResponse.data)
}

const getClassDetails = async (auth: string, activityId: ClassroomId): Promise<ClassDetails> => {
    if (process.env.REACT_APP_USE_ASSESSMENT_MOCKS) {
        const { mock } = await import('./mocks/class')
        return mock
    }

    return isBookingClassDetailsEnabled()
        ? getClassDetailsFromBookingApi(auth, activityId)
        : getClassDetailsFromBsApi(auth, activityId)
}

export { getClassDetails }
